import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

// Bare backend timestamps are UTC, but new Date() would read them as local time.
export function parseUtcTimestamp(value: unknown): string {
  const raw = pickString(value)
  if (!raw) return ''
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw)
  const parsed = hasZone ? dayjs(raw) : dayjs.utc(raw)
  return parsed.isValid() ? parsed.toISOString() : ''
}

export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export function pickString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function pickNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * A boolean from an API that does not always send one.
 *
 * The string cases are not defensive padding: these payloads arrive from a
 * serialiser that has been observed sending `"true"` for a `bool`, and
 * `Boolean("false")` is `true`, so reading it directly inverts the value.
 *
 * `fallback` matters where the safe default is not `false` — a pop-up delivery
 * setting reads `pickBool(…, true)`, because a missing key should not silently
 * mute a stage the user never turned off. That is also why numerics are NOT read
 * here: `0` could mean false or "unset", and the callers defaulting to `true`
 * chose their default for the unset case. A field that needs the numeric reading
 * narrows it locally instead.
 */
export function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}
